import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { CaseStudy } from '../../data/caseStudies';

interface CaseStudyCardProps {
  caseStudy: CaseStudy;
  index: number;
}

export const CaseStudyCard: React.FC<CaseStudyCardProps> = ({ caseStudy, index }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: index * 0.1 }}
      className="group"
    >
      <Link to={`/case-studies/${caseStudy.id}`}>
        <div className="card overflow-hidden h-full">
          <div className="relative h-48">
            <img
              src={caseStudy.image}
              alt={caseStudy.title}
              className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-300"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
            <div className="absolute bottom-4 left-4 right-4 flex items-center justify-between">
              <span className="px-3 py-1 bg-white/20 backdrop-blur-sm rounded-full text-sm text-white">
                {caseStudy.industry}
              </span>
              <img
                src={caseStudy.logo}
                alt={caseStudy.client}
                className="h-8 w-auto bg-white rounded-md p-1"
              />
            </div>
          </div>

          <div className="p-6">
            <h3 className="text-xl font-semibold mb-2 group-hover:text-primary-light dark:group-hover:text-primary-dark">
              {caseStudy.title}
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              {caseStudy.description}
            </p>

            <div className="grid grid-cols-2 gap-4 mb-6">
              {caseStudy.results.slice(0, 2).map((result, i) => (
                <div
                  key={i}
                  className="p-3 rounded-xl bg-gray-50 dark:bg-gray-800/50"
                >
                  <div className="text-sm text-gray-600 dark:text-gray-400">
                    {result.metric}
                  </div>
                  <div className="text-lg font-semibold text-primary-light dark:text-primary-dark">
                    {result.value}
                  </div>
                </div>
              ))}
            </div>

            <div className="flex flex-wrap gap-2">
              {caseStudy.technologies.slice(0, 3).map((tech, i) => (
                <span
                  key={i}
                  className="px-2 py-1 text-xs rounded-full bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400"
                >
                  {tech}
                </span>
              ))}
              {caseStudy.technologies.length > 3 && (
                <span className="px-2 py-1 text-xs rounded-full bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400">
                  +{caseStudy.technologies.length - 3} more
                </span>
              )}
            </div>

            <div className="mt-4 flex items-center text-primary-light dark:text-primary-dark font-medium">
              Read Case Study
              <svg className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </div>
          </div>
        </div>
      </Link>
    </motion.div>
  );
};
